import React, { useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, X } from 'lucide-react';
import { state } from '../../store/global';
import API from '../../lib/api';

import LoginRightWrapper from '../../components/common/LoginRightWrapper';
import LoginLeftSide from '../../components/common/LoginLeftSide';
import LoginFooter from '../../components/common/LoginFooter';
import NormalButton from '../../components/common/NormalButton';

export default function PasswordReset() {
  const snap = useSnapshot(state);
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPass, setConfirmPass] = useState('');
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const isValidator = () => {
    if (password != confirmPass) return false;
    if (
      password.length < 8 ||
      !/[A-Z]/.test(password) ||
      !/\d/.test(password) ||
      !/[^a-zA-Z0-9]/.test(password)
    )
      return false;
    return true;
  };

  const onConfirmPassword = async () => {
    const verifyToken = localStorage.getItem('verifyToken');
    if (!verifyToken) return;

    await API.post('user/resetPassword', { password, verifyToken })
      .then(() => {
        localStorage.removeItem('verifyToken');
        navigate('/forgotPassword/success');
      })
      .catch((err) => {
        console.log('err: ', err);
      });
  };
  const onGoToPage = (path) => {
    navigate(path);
  };

  return (
    <div className="flex min-h-screen">
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        {/* Modal content with exact size */}
        <div className="relative z-10 bg-white rounded-2xl shadow-xl w-[1044px] h-[700px] overflow-hidden">
          <div className="flex flex-col md:flex-row w-full h-full">
            {/* Left Side - Image */}
            <LoginLeftSide />

            {/* Right Side - Form */}
            <div className="w-full h-full flex flex-col justify-between md:w-1/2 p-10 bg-[#F9F9F9]">
              <LoginRightWrapper
                onGoToPage={(path) => onGoToPage(path)}
                backPath="/forgotPassword"
              />

              <div>
                <div className="space-y-8 font-[poppins] font-semibold pl-0 pr-0 md:pl-14 md:pr-14">
                  <div className="space-y-4">
                    <div className="space-y-4">
                      <p className="text-xl text-[#333333] font-bold leading-[20px] tracking-[0%]">
                        Crear nueva contraseña
                      </p>
                      <p className="text-sm text-[#717680] font-normal leading-[20px] tracking-[0%]">
                        Ingresa tu nueva contraseña
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-1">
                        <div className="text-xs text-[#717680] font-normal leading-[20px] tracking-[0%]">
                          Contraseña
                        </div>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Contraseña123#"
                            className="w-full text-sm placeholder-[#A4A7AE] text-[#414651] font-normal focus:outline-none focus:border-[#414651] bg-white border border-[#C2C2C2] py-2 pl-3 pr-10 rounded-[10px]"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-2.5 top-2.5 text-gray-500"
                          >
                            {showPassword ? (
                              <EyeOff size={16} />
                            ) : (
                              <Eye size={16} />
                            )}
                          </button>
                        </div>

                        <p className="text-xs font-normal leading-[20px]">
                          <span
                            className={
                              password.length < 8
                                ? 'text-[#A4A7AE]'
                                : 'text-[#027A48]'
                            }
                          >{`Incluir ${
                            password.length < 8 ? '•' : '✓'
                          }8 caractéres`}</span>
                          <span
                            className={
                              /[A-Z]/.test(password)
                                ? 'text-[#027A48]'
                                : 'text-[#A4A7AE]'
                            }
                          >{` o más ${
                            /[A-Z]/.test(password) ? '✓' : '•'
                          } mayúscula`}</span>
                          <span
                            className={
                              /\d/.test(password)
                                ? 'text-[#027A48]'
                                : 'text-[#A4A7AE]'
                            }
                          >{` ${
                            /\d/.test(password) ? '✓' : '•'
                          } 1 número`}</span>
                          <span
                            className={
                              /[^a-zA-Z0-9]/.test(password)
                                ? 'text-[#027A48]'
                                : 'text-[#A4A7AE]'
                            }
                          >{` ${
                            /[^a-zA-Z0-9]/.test(password) ? '✓' : '•'
                          } 1 símbolo especial ( !, # ó $).`}</span>
                        </p>
                      </div>

                      <div className="space-y-1">
                        <div className="text-xs text-[#717680] font-normal leading-[20px] tracking-[0%]">
                          Repetir nueva contraseña
                        </div>
                        <div className="relative">
                          <input
                            type={showConfirmPass ? 'text' : 'password'}
                            value={confirmPass}
                            onChange={(e) => setConfirmPass(e.target.value)}
                            placeholder="Contraseña123#"
                            className="w-full text-sm placeholder-[#A4A7AE] text-[#414651] font-normal focus:outline-none focus:border-[#414651] bg-white border border-[#C2C2C2] py-2 pl-3 pr-10 rounded-[10px]"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPass((prev) => !prev)}
                            className="absolute right-2.5 top-2.5 text-gray-500"
                          >
                            {showConfirmPass ? (
                              <EyeOff size={16} />
                            ) : (
                              <Eye size={16} />
                            )}
                          </button>
                        </div>

                        <p className="text-xs font-normal leading-[20px]">
                          <span
                            className={
                              password && password == confirmPass
                                ? 'text-[#027A48]'
                                : 'text-[#A4A7AE]'
                            }
                          >{`${
                            password && password == confirmPass ? '✓' : '•'
                          } Coincidencia de contraseñas`}</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <NormalButton
                      disabled={isValidator() ? false : true}
                      customClass={`w-full ${
                        isValidator()
                          ? 'bg-[#092F7B] text-[#FDFDFD]'
                          : 'bg-[#F5F5F5] text-[#A4A7AE] border border-[#E9EAEB]'
                      }`}
                      text="Confirmar nueva contraseña"
                      onClick={onConfirmPassword}
                    />
                    <div className="h-10 w-full bg-[#F9F9F9]"></div>
                  </div>
                </div>
              </div>

              {/* Login footer */}
              <LoginFooter onClick={() => navigate('/contact')} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
